"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface CreditSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  credits: number;
  transactionId: string;
  currentBalance?: number;
}

export default function CreditSuccessModal({
  isOpen,
  onClose,
  credits,
  transactionId,
  currentBalance,
}: CreditSuccessModalProps) {
  const router = useRouter();

  const handleStartCreating = () => {
    onClose();
    router.push("/create");
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent showCloseButton={true}>
        <div className="relative overflow-hidden">
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-success-muted via-success-muted/50 to-primary-muted opacity-80 animate-pulse" />

          {/* Floating celebration particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-4 right-6 w-3 h-3 bg-success rounded-full animate-bounce delay-100" />
            <div className="absolute top-12 left-8 w-2 h-2 bg-primary rounded-full animate-bounce delay-300" />
            <div className="absolute bottom-8 right-12 w-2.5 h-2.5 bg-success rounded-full animate-bounce delay-500" />
            <div className="absolute bottom-16 left-12 w-1.5 h-1.5 bg-primary rounded-full animate-bounce delay-700" />
            <div className="absolute top-20 right-16 w-2 h-2 bg-success rounded-full animate-bounce delay-1000" />
          </div>

          <div className="relative text-center space-y-6 px-2 py-4">
            {/* Success icon with enhanced animation */}
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-success shadow-xl transform transition-all duration-700 hover:scale-110 hover:shadow-2xl">
              <div className="h-20 w-20 rounded-full bg-success-foreground/30 backdrop-blur-sm flex items-center justify-center border-2 border-success-foreground/20">
                <Check
                  className="h-10 w-10 text-success-foreground animate-[checkmark_0.8s_ease-out] drop-shadow-lg"
                  strokeWidth={3}
                />
              </div>
            </div>

            {/* Main content with improved typography */}
            <div className="space-y-6">
              <div className="space-y-3">
                <h2 className="text-3xl font-bold text-foreground">
                  Credits added
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  <span className="font-bold text-primary text-xl">
                    {credits} credits
                  </span>{" "}
                  have been added to your account
                </p>
              </div>

              {/* Enhanced balance display */}
              {currentBalance !== undefined && (
                <div className="bg-gradient-to-br from-card to-success-muted backdrop-blur-sm rounded-2xl p-8 border-2 border-success/30 shadow-lg transform transition-all duration-300 hover:shadow-xl hover:scale-[1.02]">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                    <p className="text-sm text-foreground font-semibold uppercase tracking-wide">
                      Your new balance
                    </p>
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse delay-300" />
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-foreground mb-1">
                      {currentBalance}
                    </p>
                    <p className="text-lg text-success font-medium">
                      credits available
                    </p>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  onClick={handleStartCreating}
                  variant="default"
                  size="lg"
                  className="flex-1"
                >
                  Start designing
                  <ArrowRight className="size-5" />
                </Button>
                <Button
                  onClick={onClose}
                  variant="secondary"
                  size="lg"
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>

            {/* Transaction ID - more subtle and styled */}
            {transactionId && (
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground font-mono bg-muted px-3 py-1 rounded-full inline-block">
                  Transaction #{transactionId}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced animation keyframes */}
        <style jsx>{`
          @keyframes checkmark {
            0% {
              transform: scale(0) rotate(45deg);
              opacity: 0;
            }
            50% {
              transform: scale(1.3) rotate(45deg);
              opacity: 1;
            }
            100% {
              transform: scale(1) rotate(0deg);
              opacity: 1;
            }
          }

          @keyframes float {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
